// BroadcastDashboard.jsx
//
// Device-side variant of the dashboard (Phase D of BroadcastBoard):
// plays the streamed board as a fullscreen YouTube embed, with LOCAL
// overlays for everything device-specific:
//   - family name in the header (streamed header has that line empty)
//   - VacationCountdownTile at its exact frame position
//   - LiveStreamTile at its exact frame position (dual embed = the
//     proven multiview load)
//   - EXIT button (streamed board has none)
//
// The stream is 1920x1080: the board occupies the central 1728px with
// 96px BG bars each side. Oversize-and-crop (same trick as
// LiveStreamTile's -8%/116%): iframe = 111.111% wide, shifted left
// -5.5556%, so the bars fall off-screen and the board lands 1:1 on the
// 1280x800 display.
//
// videoId comes from RTDB broadcast/videoId — never hardcoded. If the
// YouTube broadcast ever gets a new ID, update that one RTDB value.
//
// Stream-down fallback: player onEnded/onError -> static styled frame
// (local tiles keep working), retry remount every 60s, and any RTDB
// videoId change retries immediately.
//
// Overlay frame coordinates measured from the actual streamed frame:
//   vacation: x754 y387 248x130   live: x754 y531 510x253

import { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../../config/firebaseConfig";
import { useRemote } from "../../hooks/useRemote";
import useGlobalStore from "../../store/global.store";
import YouTubePlayer from "./MultiView/YouTubePlayer";
import VacationCountdownTile from "./VacationCountdownTile";
import BroadcastLiveTile from "./BroadcastLiveTile";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";

const BG = "#e3e8ef";
const HEADER_NAVY = "#1a3054";
const EXIT_RED = "#dc2626";
const RETRY_MS = 60 * 1000;
// Live HLS board player — the proven VOD player (Clappr), chromeless + muted.
const HLS_PLAYER = "https://vod.mywaitserver.live/player5.html";

// Oversize-and-crop for the 96px stream bars: 1920/1728 and 96/1728.
const EMBED_W = "111.111%";
const EMBED_LEFT = "-5.5556%";

const VACATION_FRAME = { left: 754, top: 387, width: 248, height: 130 };
const LIVE_FRAME = { left: 754, top: 531, width: 510, height: 253 };

const BroadcastDashboard = ({ device, countDownDate, timezone, currentDate }) => {
  const { updateView } = useRemote();
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);

  // Hide the global BottomBar while open; restore on exit (same as Dashboard).
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  useEffect(() => {
    setShowBottomBar(false);
    return () => setShowBottomBar(true);
  }, [setShowBottomBar]);

  // ── Broadcast videoId from RTDB ─────────────────────────────────────
  const [broadcastVideoId, setBroadcastVideoId] = useState("");
  const [hlsUrl, setHlsUrl] = useState("");
  const [streamDown, setStreamDown] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  // ── Live-tile audio state (persisted): mode / mute / volume ─────────
  // Defaults: music mode, unmuted, volume 20 (the old shared dash level).
  const [tileMode, setTileMode] = useState(() => {
    const saved = localStorage.getItem("bdTileMode");
    return saved === "park" ? "park" : "music";
  });
  const [tileMuted, setTileMuted] = useState(
    () => localStorage.getItem("bdTileMuted") === "1"
  );
  const [tileVolume, setTileVolume] = useState(() => {
    const v = parseInt(localStorage.getItem("bdTileVolume"), 10);
    return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 20;
  });
  useEffect(() => {
    localStorage.setItem("bdTileMode", tileMode);
    localStorage.setItem("bdTileMuted", tileMuted ? "1" : "0");
    localStorage.setItem("bdTileVolume", String(tileVolume));
  }, [tileMode, tileMuted, tileVolume]);
  const toggleTileMode = () =>
    setTileMode((m) => (m === "music" ? "park" : "music"));
  // VOL+ also un-mutes, so a muted user who raises volume hears something.
  const volumeUp = () => {
    setTileMuted(false);
    setTileVolume((v) => Math.min(100, v + 10));
  };
  const volumeDown = () => setTileVolume((v) => Math.max(0, v - 10));
  const toggleTileMute = () => setTileMuted((m) => !m);
  // Same button chrome as the old dash header volume cluster.
  const volBtnStyle = (extra = {}) => ({
    background: "#4f5051",
    color: "#fff",
    width: 52,
    height: 52,
    borderRadius: 10,
    border: "3px solid #fafbfa",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    outline: "none",
    userSelect: "none",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    pointerEvents: "auto",
    ...extra,
  });

  useEffect(() => {
    const r = ref(database, "broadcast");
    const h = onValue(r, (snap) => {
      const v = snap.exists() ? snap.val() : {};
      setBroadcastVideoId(v && v.videoId ? String(v.videoId) : "");
      setHlsUrl(v && v.hlsUrl ? String(v.hlsUrl) : "");
      // A fresh/changed ID is a reason to try again immediately.
      setStreamDown(false);
    });
    return () => {
      off(r);
      if (typeof h === "function") h();
    };
  }, []);

  // ── Stream-down fallback + 60s retry ────────────────────────────────
  const retryTimerRef = useRef(null);
  const handleStreamDown = () => {
    setStreamDown(true);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      setRetryNonce((n) => n + 1); // new key -> full player remount
      setStreamDown(false);
    }, RETRY_MS);
  };
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);
  // ── HLS health watchdog ─────────────────────────────────────────────
  // player5 posts {vodtime:{cur,dur}} while the video plays. If position
  // stops ADVANCING for 15s (paused / stalled / dead — e.g. a stream gap
  // strands Clappr on its play button once the 8s autoplay kick expires),
  // remount the iframe via retryNonce. Retries forever until healthy.
  const lastProgressRef = useRef({ cur: -1, at: Date.now() });
  const liveCheckRef = useRef({ last: -1, count: 0 });
  useEffect(() => {
    const onMsg = (ev) => {
      const t = ev && ev.data && ev.data.vodtime;
      if (t && typeof t.cur === "number" && t.cur !== lastProgressRef.current.cur) {
        lastProgressRef.current = { cur: t.cur, at: Date.now() };
        // Declare live only after SUSTAINED playback — three consecutive
        // advancing ticks (~1.5s of real motion). A single position jump
        // is just hls.js seeking to the live edge, which happens BEFORE
        // frames actually render on these boxes.
        if (!streamLiveRef.current) {
          if (t.cur > liveCheckRef.current.last) {
            liveCheckRef.current.count += 1;
          } else {
            liveCheckRef.current.count = 0;
          }
          liveCheckRef.current.last = t.cur;
          if (liveCheckRef.current.count >= 3) {
            streamLiveRef.current = true;
            everLiveRef.current = true;
            setStreamLive(true);
          }
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => {
    if (!hlsUrl) return;
    lastProgressRef.current = { cur: -1, at: Date.now() }; // mount grace
    const iv = setInterval(() => {
      if (Date.now() - lastProgressRef.current.at > 15000) {
        lastProgressRef.current = { cur: -1, at: Date.now() };
        // Cover the retry with the loading screen.
        streamLiveRef.current = false;
        liveCheckRef.current = { last: -1, count: 0 };
        setStreamLive(false);
        setRetryNonce((n) => n + 1);
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [hlsUrl, retryNonce]);
  // ── Magical loading overlay state ───────────────────────────────────
  // Visible until the board video provably plays; returns during watchdog
  // retries. Bar crawls toward 90%, ZIPS to 100 on the live signal, then
  // the overlay dissolves and unmounts.
  const [streamLive, setStreamLive] = useState(false);
  const streamLiveRef = useRef(false);
  // Splash art: first load = "preparing", watchdog-retry reappearances =
  // "updating park data". Chosen at overlay (re)appearance, never mid-fade.
  const everLiveRef = useRef(false);
  const [splashUpdate, setSplashUpdate] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.src = "/dash_update_display_night.jpg"; // pre-warm for first retry
  }, []);
  const [loadPct, setLoadPct] = useState(0);
  const [overlayGone, setOverlayGone] = useState(false);
  const pRef = useRef(0);
  const overlayShownAtRef = useRef(Date.now());
  useEffect(() => {
    if (streamLive) {
      // 3s minimum splash — instant loads otherwise flash weirdly. The
      // crawl keeps moving through the wait, then zips.
      const wait = Math.max(0, 3000 - (Date.now() - overlayShownAtRef.current));
      const iv = setInterval(() => {
        pRef.current += (90 - pRef.current) * 0.035;
        setLoadPct(Math.min(90, Math.floor(pRef.current)));
      }, 120);
      const t0 = setTimeout(() => {
        clearInterval(iv);
        setLoadPct(100); // zip
      }, wait);
      const t1 = setTimeout(() => setOverlayGone(true), wait + 1500);
      return () => {
        clearInterval(iv);
        clearTimeout(t0);
        clearTimeout(t1);
      };
    }
    overlayShownAtRef.current = Date.now();
    setSplashUpdate(everLiveRef.current);
    setOverlayGone(false);
    setLoadPct(0);
    pRef.current = 0;
    const iv = setInterval(() => {
      pRef.current += (90 - pRef.current) * 0.035;
      setLoadPct(Math.min(90, Math.floor(pRef.current)));
    }, 120);
    return () => clearInterval(iv);
  }, [streamLive]);
  const loadTwinkles = useMemo(() => {
    const arr = [];
    while (arr.length < 28) {
      const x = Math.random() * 1280;
      const y = Math.pow(Math.random(), 1.4) * 330; // sky only, denser up top
      if (x > 545 && x < 735 && y > 180) continue; // keep the logo clear
      arr.push({
        x,
        y,
        s: Math.random() < 0.12 ? 3 : Math.random() < 0.5 ? 1.6 : 2.2,
        tw: (2.6 + Math.random() * 3.8).toFixed(2),
        td: (-Math.random() * 6).toFixed(2),
        o: (0.35 + Math.random() * 0.4).toFixed(2),
      });
    }
    return arr;
  }, []);

  const showStream = Boolean(hlsUrl || broadcastVideoId) && !streamDown;
  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        background: BG,
        overflow: "hidden",
      }}
    >
      {/* ── LAYER 0: fullscreen streamed board ── */}
      {showStream && hlsUrl ? (
        // HLS via the proven VOD player — native 1280x800, no crop, no offset
        <iframe
          key={`${hlsUrl}-${retryNonce}`}
          title="Broadcast Board"
          src={`${HLS_PLAYER}?ui=tv&vol=0&src=${encodeURIComponent(hlsUrl)}`}
          allow="autoplay"
          style={{
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            border: "none",
            pointerEvents: "none",
          }}
        />
      ) : showStream ? (
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: EMBED_LEFT,
              width: EMBED_W,
              height: "100%",
            }}
          >
            <YouTubePlayer
              key={`${broadcastVideoId}-${retryNonce}`}
              videoId={broadcastVideoId}
              isSelected={false}
              onEnded={handleStreamDown}
              onError={handleStreamDown}
            />
          </div>
        </div>
      ) : (
        // ── Static fallback frame: board look, no data ──
        <div style={{ position: "absolute", inset: 0, background: BG }}>
          <div
            style={{
              position: "absolute",
              top: 300,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: "'Lato', sans-serif",
              color: "#1a3054",
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Board updating — back in a moment
          </div>
        </div>
      )}

      {/* ── LAYER 1: tap shield — taps never reach the YouTube iframe ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 10,
          background: "transparent",
        }}
      />

      {/* ── LAYER 2: local overlays ── */}
      {/* Header replica — transparent bg (streamed navy shows through on
          stream; solid navy on fallback so the header always looks right) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 86,
          zIndex: 20,
          background: showStream ? "transparent" : HEADER_NAVY,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 28px",
          boxSizing: "border-box",
          pointerEvents: "none",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          {/* Spacer copy of the streamed small line, so the name lands on
              the exact same baseline as the original Dashboard */}
          <span
            style={{
              visibility: "hidden",
              fontFamily: "Lato, sans-serif",
              fontSize: 23,
              fontWeight: 500,
            }}
          >
            Have a Magical Day
          </span>
          <span
            style={{
              color: "#fff",
              fontFamily: "Lato, sans-serif",
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {device.subscription?.last_name} Family
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {/* Mode switch — VIP only; non-VIP is music-only */}
          {hasVipAddOn && (
          <button
            onClick={toggleTileMode}
            style={{
              pointerEvents: "auto",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              border: "3px solid rgba(255,255,255,0.85)",
              borderRadius: 12,
              height: 52,
              boxSizing: "border-box",
              padding: "0 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Lato, sans-serif",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              outline: "none",
              letterSpacing: 0.6,
              whiteSpace: "nowrap",
            }}
          >
            {tileMode === "music"
              ? "SWITCH TO PARK STREAMERS"
              : "SWITCH TO MUSIC STREAMERS"}
          </button>
          )}

          {/* Volume cluster — identical chrome to the old dash header */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={volumeDown} tabIndex={-1} style={volBtnStyle()}>
              <LuVolume1 size={34} />
            </button>
            <button onClick={volumeUp} tabIndex={-1} style={volBtnStyle()}>
              <LuVolume2 size={34} />
            </button>
            <button
              onClick={toggleTileMute}
              tabIndex={-1}
              style={volBtnStyle({
                borderColor: tileMuted ? "red" : "#05f651",
                color: tileMuted ? "red" : "#05f651",
              })}
            >
              <LuVolumeOff size={34} />
            </button>
          </div>

          <button
            onClick={() => updateView("Home")}
            style={{
              pointerEvents: "auto",
              background: EXIT_RED,
              color: "#fff",
              border: "3px solid #fff",
              borderRadius: 12,
              height: 52,
              boxSizing: "border-box",
              padding: "0 30px",
              display: "flex",
              alignItems: "center",
              fontFamily: "Lato, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              cursor: "pointer",
              outline: "none",
              letterSpacing: 1,
            }}
          >
            EXIT
          </button>
        </div>
      </div>

      {/* Vacation countdown — local, at its measured frame position */}
      <div
        style={{
          position: "absolute",
          zIndex: 20,
          pointerEvents: "none",
          ...VACATION_FRAME,
        }}
      >
        <VacationCountdownTile
          countDownDate={countDownDate}
          timezone={timezone}
          currentDate={currentDate}
        />
      </div>

      {/* Live stream tile — local, dual-embed leg, keeps its own taps */}
      <div
        style={{
          position: "absolute",
          zIndex: 20,
          pointerEvents: "auto",
          ...LIVE_FRAME,
        }}
      >
        <BroadcastLiveTile
          mode={hasVipAddOn ? tileMode : "music"}
          muted={tileMuted}
          volume={tileVolume}
          timezone={timezone}
        />
      </div>

      {/* ── Magical loading overlay: covers the board until the video is
          provably playing; returns during watchdog retries. Header + tiles
          (z 20) stay above it — EXIT keeps working and music keeps playing
          through any outage. ── */}
      {!overlayGone && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 40,
            background: splashUpdate
              ? "#141945 url('/dash_update_display_night.jpg') center / 1280px 800px no-repeat"
              : "#141945 url('/dash_load_display_night.jpg') center / 1280px 800px no-repeat",
            opacity: loadPct >= 100 ? 0 : 1,
            transition: "opacity 0.9s ease 0.3s",
            pointerEvents: "none",
          }}
        >
          <style>{`
            @keyframes bdTw{0%,100%{opacity:.06;}50%{opacity:var(--o,.7);}}
            @keyframes bdHalo{0%,100%{transform:scale(.9);opacity:.8;}50%{transform:scale(1.12);opacity:1;}}
            @keyframes bdSweep{0%{background-position:130% 0;}100%{background-position:-130% 0;}}
          `}</style>

          {loadTwinkles.map((t, i) => (
            <span
              key={i}
              style={{
                position: "absolute",
                left: t.x,
                top: t.y,
                width: t.s,
                height: t.s,
                borderRadius: "50%",
                background: "#fff",
                boxShadow:
                  t.s >= 3 ? "0 0 7px 2px rgba(255,255,255,.35)" : "none",
                animation: `bdTw ${t.tw}s ease-in-out ${t.td}s infinite`,
                "--o": t.o,
              }}
            />
          ))}

          {/* star-head glow — centered on the image's star (860,128) */}
          <div
            style={{ position: "absolute", left: 860, top: 128, width: 0, height: 0 }}
          >
            <span
              style={{
                position: "absolute",
                left: -70,
                top: -70,
                width: 140,
                height: 140,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,220,150,.4) 0%, rgba(255,200,120,.14) 40%, rgba(255,200,120,0) 68%)",
                animation: "bdHalo 2.6s ease-in-out infinite",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: -32,
                top: -32,
                width: 64,
                height: 64,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,246,225,.7) 0%, rgba(255,220,150,.28) 45%, rgba(255,220,150,0) 70%)",
                animation: "bdHalo 2.6s ease-in-out -1.3s infinite",
              }}
            />
          </div>

          {/* magical loading bar */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: 550,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                position: "relative",
                width: 440,
                height: 15,
                borderRadius: 10,
                overflow: "hidden",
                background: "rgba(10,12,40,.55)",
                boxShadow:
                  "inset 0 1px 4px rgba(0,0,0,.5), 0 0 0 1px rgba(190,170,255,.18), 0 0 18px rgba(120,90,220,.25)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${loadPct}%`,
                  borderRadius: 10,
                  background:
                    "linear-gradient(90deg,#f0c060 0%, #e8b06a 30%, #b592f5 70%, #8f6df0 100%)",
                  boxShadow: "0 0 14px rgba(240,192,96,.45)",
                  transition: streamLive
                    ? "width .35s ease-out"
                    : "width .18s linear",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(105deg, transparent 30%, rgba(255,255,255,.45) 50%, transparent 70%)",
                    backgroundSize: "220% 100%",
                    animation: "bdSweep 1.8s ease-in-out infinite",
                  }}
                />
              </div>
            </div>
            <span
              style={{
                color: "#ffe0a8",
                font: "700 19px Lato, sans-serif",
                fontVariantNumeric: "tabular-nums",
                minWidth: 54,
                textShadow: "0 0 12px rgba(240,192,96,.5)",
              }}
            >
              {loadPct}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastDashboard;
